import { useWorkspace } from '@/contexts/workspace-context';
import {
  Permission,
  createWorkspacePermissions,
  WorkspacePermissions,
} from '@optidev/shared-types';

/**
 * Hook for checking user permissions in components
 *
 * Uses shared `createWorkspacePermissions()` factory for consistency with backend.
 *
 * Usage:
 * ```tsx
 * const { can, isViewer, canEditFiles } = usePermissions();
 *
 * // Check specific permission
 * if (!can(Permission.PROJECT_CREATE)) {
 *   return null;
 * }
 *
 * // Use convenience props
 * <Button disabled={!canEditFiles}>Edit</Button>
 * ```
 */
export function usePermissions(): WorkspacePermissions {
  const { activeWorkspace, currentWorkspace } = useWorkspace();

  // Platform admin viewing a non-member workspace: backend (`workspace-members.service.ts`
  // `getUserWorkspaces`) only sets `currentWorkspace` when the requesting user is a
  // platform admin AND the active workspace is one they don't belong to. In that case
  // grant owner-level permissions so the IDE/chat/visual-editor surfaces aren't gated
  // (backend already gives admins full access via PermissionGuard).
  if (currentWorkspace && !activeWorkspace) {
    return createWorkspacePermissions({ role: 'admin', isOwner: true });
  }

  // Use shared factory for consistency with backend PermissionsService
  return createWorkspacePermissions({
    role: activeWorkspace?.role,
    isOwner: activeWorkspace?.isOwner,
  });
}

// Re-export Permission for convenience
export { Permission };
